// Glowy Mail stüdyosu — e-posta tasarım belgesi ve HTML derleyici.
//
// Belge: { theme, blocks: [{ id, type, ...alanlar }] }. Bloklar alt alta dizilir (e-postada serbest
// konum yok; Outlook/Gmail mutlak konumu bozar). renderEmail() tablo düzenli, satır içi stilli HTML
// üretir; kaydedilen şablonun html'i budur, gönderim tarafı (campaignSender) yalnız {{etiket}} doldurur.
//
// Görseller: ürün CDN'leri çoğunlukla webp; Outlook masaüstü göstermez. Kaydetmeden önce
// collectImages() ile eksik JPG'ler /email-platform/images'a çevrilir ve item.jpg / item.jpgWide'a yazılır.

export const uid = () => Math.random().toString(36).slice(2, 9);

export const DEFAULT_THEME = {
  logo: '',
  logoWidth: 96,
  ink: '#0b0b0b',
  soft: '#F6F6F6',
  muted: '#6b6b6b',
  line: '#e3e3e3',
  accent: '#0b0b0b',
  radius: 0,
  headFont: 'Anton',
  uppercaseHeadings: true,
  nav: [],
  footerText: 'Bu e-postayı ticari ileti almayı kabul ettiğin için alıyorsun.',
  footerLinks: [],
  utmSource: 'glowytics',
  // Editoryal seçenekler (boş = eski görünüm): başlık kalınlığı/harf aralığı, buton biçimi, açık footer, dış zemin
  headWeight: 400,
  headTracking: null,
  buttonStyle: 'solid',   // solid | outline | link
  footerStyle: 'dark',    // dark | light
  pageBg: '#e9e9e9',
};

export const HEAD_FONTS = {
  Anton: { css: "'Anton','Oswald','Arial Narrow',Impact,Arial,sans-serif", url: 'https://fonts.googleapis.com/css2?family=Anton&display=swap' },
  Oswald: { css: "'Oswald','Arial Narrow',Arial,sans-serif", url: 'https://fonts.googleapis.com/css2?family=Oswald:wght@600&display=swap' },
  Inter: { css: "'Inter',Helvetica,Arial,sans-serif", url: 'https://fonts.googleapis.com/css2?family=Inter:wght@800&display=swap' },
  Jost: { css: "'Futura','Jost','Century Gothic',Arial,sans-serif", url: 'https://fonts.googleapis.com/css2?family=Jost:wght@400&display=swap' },
  'Maven Pro': { css: "'Maven Pro','Century Gothic',Helvetica,Arial,sans-serif", url: 'https://fonts.googleapis.com/css2?family=Maven+Pro:wght@500&display=swap' },
  'Playfair Display': { css: "'Playfair Display',Georgia,serif", url: 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&display=swap' },
  Arial: { css: 'Arial,Helvetica,sans-serif', url: null },
};
const BODY = "'Helvetica Neue',Helvetica,Arial,sans-serif";

// Zemin seçenekleri: tema renklerine bağlı adlar ya da düz hex
export const BG_OPTIONS = [['white', 'White'], ['soft', 'Soft'], ['ink', 'Dark']];
const bgColor = (bg, t) => (bg === 'soft' ? t.soft : bg === 'ink' ? t.ink : bg && bg.startsWith('#') ? bg : '#ffffff');
const isDark = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return false;
  const n = parseInt(m[1], 16);
  return (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) < 140;
};

export const BLOCK_TYPES = {
  hero: { label: 'Headline', hint: 'Eyebrow, big title, text and a button' },
  image: { label: 'Image', hint: 'Full-width product or campaign image' },
  cover: { label: 'Cover', hint: 'Full-width photo with a headline below' },
  split: { label: 'Split', hint: 'Photo on one side, text on the other' },
  products: { label: 'Product grid', hint: '2 columns, with price and buy button' },
  compare: { label: 'Compare', hint: 'Two products side by side with a spec table' },
  steps: { label: 'Steps', hint: 'Numbered tips or checklist' },
  text: { label: 'Text', hint: 'Paragraph or small note' },
  button: { label: 'Button', hint: 'Single call to action' },
  spacer: { label: 'Spacer', hint: 'Empty space' },
};

export function newBlock(type) {
  const base = { id: uid(), type };
  switch (type) {
    case 'hero': return { ...base, bg: 'white', eyebrow: 'Yeni sezon', title: 'Başlık buraya', text: 'Kısa bir açıklama yaz.', button: { label: 'Alışverişe başla', url: '' }, titleSize: 46 };
    case 'image': return { ...base, item: null, src: '', url: '', crop: 'wide' };
    case 'cover': return { ...base, bg: 'white', item: null, src: '', url: '', eyebrow: '', title: 'Başlık buraya', text: '', link: { label: 'Keşfedin', url: '' }, titleSize: 34, align: 'center' };
    case 'split': return { ...base, bg: 'soft', item: null, src: '', url: '', side: 'left', eyebrow: '', title: 'Başlık', text: 'Kısa bir açıklama.', link: { label: 'İncele', url: '' } };
    case 'products': return { ...base, bg: 'white', title: 'Senin için seçtik', items: [], buyLabel: 'Satın al' };
    case 'compare': return { ...base, a: null, b: null, labelA: 'Şu anki', labelB: 'Bir üst seviye', rows: [['Seviye', '', '']], buyLabel: 'Satın al' };
    case 'steps': return { ...base, bg: 'soft', title: 'Üç adım', items: [{ title: 'Birinci adım', text: 'Açıklama' }, { title: 'İkinci adım', text: 'Açıklama' }] };
    case 'text': return { ...base, bg: 'white', text: 'Metin buraya.', small: false };
    case 'button': return { ...base, bg: 'white', label: 'Hepsini gör', url: '' };
    case 'spacer': return { ...base, bg: 'white', height: 24 };
    default: return base;
  }
}

// ── yardımcılar ─────────────────────────────────────────────────────────────
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// Metin alanları: kaçışlanır, satır sonu <br>'ye döner, {{etiket}} olduğu gibi kalır.
// İngilizce model adları büyük harfte "İ" olmasın diye etiket ve **kalın** dışındaki her şey lang=tr
// kalır; {{...}} etiketleri lang=en sarılır (seri/model adı çoğunlukla İngilizce).
function rich(s) {
  return esc(s)
    .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
    .replace(/\{\{([^}]+)\}\}/g, (m) => `<span lang="en">${m}</span>`)
    .replace(/\n/g, '<br>');
}

export function money(n) {
  if (n == null || n === '') return '';
  const v = Number(n);
  const s = v.toLocaleString('tr-TR', { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 });
  return `${s} TL`;
}

function withUtm(url, t, campaign) {
  if (!url) return '#';
  if (/^\{\{/.test(url) || url.startsWith('#') || url.startsWith('mailto:')) return url;
  try {
    const u = new URL(url);
    if (!u.searchParams.has('utm_source')) {
      u.searchParams.set('utm_source', t.utmSource || 'glowytics');
      u.searchParams.set('utm_medium', 'email');
      if (campaign) u.searchParams.set('utm_campaign', campaign);
    }
    return u.toString();
  } catch { return url; }
}

export const slugify = (s) => String(s || '').toLowerCase()
  .replace(/[çğıöşü]/g, (c) => ({ ç: 'c', ğ: 'g', ı: 'i', ö: 'o', ş: 's', ü: 'u' }[c]))
  .replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 60);

const imgOf = (item, wide) => (wide ? item?.jpgWide || item?.jpg : item?.jpg) || item?.image || '';
const tallOf = (item) => item?.jpgTall || item?.jpg || item?.image || '';
// cover / split: ürün ya da serbest görsel (src, varsa çevrilmiş jpg)
const picOf = (b, tall) => (b.item ? (tall ? tallOf(b.item) : imgOf(b.item, true)) : b.jpg || b.src || '');

// ── bloklar → HTML ─────────────────────────────────────────────────────────
function ctx(doc, opts) {
  const t = { ...DEFAULT_THEME, ...(doc.theme || {}) };
  const head = (HEAD_FONTS[t.headFont] || HEAD_FONTS.Anton).css;
  const r = Number(t.radius) || 0;
  // başlık stili: tema boşsa bloğun eski varsayılan harf aralığı
  const hstyle = (defTrack) => `font-weight:${t.headWeight || 400};letter-spacing:${t.headTracking ?? defTrack}px;${t.uppercaseHeadings ? 'text-transform:uppercase;' : ''}`;
  return { t, head, r, hstyle, link: (u) => esc(withUtm(u, t, opts.campaign)) };
}

function button(c, label, url, onDark) {
  if (c.t.buttonStyle === 'link') return textLink(c, label, url, onDark);
  if (c.t.buttonStyle === 'outline') {
    const fg = onDark ? '#ffffff' : c.t.ink;
    return `<table role="presentation" cellpadding="0" cellspacing="0"><tr><td style="border:1px solid ${fg};border-radius:${c.r ? 999 : 0}px;">
<a href="${c.link(url)}" style="display:inline-block;padding:14px 34px;font:500 12px/1 ${BODY};letter-spacing:3px;color:${fg};text-decoration:none;text-transform:uppercase;">${rich(label)}</a></td></tr></table>`;
  }
  const bg = onDark ? '#ffffff' : c.t.accent;
  const fg = onDark ? c.t.ink : '#ffffff';
  return `<table role="presentation" cellpadding="0" cellspacing="0"><tr><td style="background:${bg};border-radius:${c.r ? 999 : 0}px;">
<a href="${c.link(url)}" style="display:inline-block;padding:15px 30px;font:700 13px/1 ${BODY};letter-spacing:2px;color:${fg};text-decoration:none;text-transform:uppercase;">${rich(label)}</a></td></tr></table>`;
}

// İnce alt çizgili metin linki (editoryal buton)
function textLink(c, label, url, onDark) {
  const fg = onDark ? '#ffffff' : c.t.ink;
  return `<a href="${c.link(url)}" style="display:inline-block;font:500 12px/1 ${BODY};letter-spacing:3px;color:${fg};text-decoration:none;text-transform:uppercase;border-bottom:1px solid ${fg};padding:0 0 6px;">${rich(label)}</a>`;
}

// Editoryal ürün kartı: kenardan kenara dikey görsel, küçük harf aralıklı ad, fiyat, "İncele" linki
function editorialCell(c, item, label, linkLabel, bg) {
  if (!item) return `<div style="background:${c.t.soft};height:400px;"></div>`;
  const lab = label ? `<div style="font:500 10px/1 ${BODY};letter-spacing:2.5px;color:${c.t.muted};text-transform:uppercase;margin:0 0 8px;">${rich(label)}</div>` : '';
  const price = item.price != null ? `<div style="font:400 13px/1.4 ${BODY};color:${c.t.ink};margin:6px 0 0;">${money(item.price)}`
    + (item.listPrice ? ` <span style="color:#9a9a9a;text-decoration:line-through;">${money(item.listPrice)}</span>` : '') + '</div>' : '';
  const more = linkLabel ? `<div style="margin:14px 0 0;">${textLink(c, linkLabel, item.url, isDark(bg))}</div>` : '';
  return `<a href="${c.link(item.url)}" style="text-decoration:none;"><img class="fimg" src="${esc(tallOf(item))}" width="300" alt="${esc(item.title)}" style="display:block;border:0;width:100%;height:auto;background:${c.t.soft};"></a>
<div style="padding:16px 22px 30px;text-align:left;">${lab}<a href="${c.link(item.url)}" style="font:500 12px/1.5 ${BODY};letter-spacing:1.5px;color:${c.t.ink};text-decoration:none;text-transform:uppercase;">${esc(item.title)}</a>${price}${more}</div>`;
}

// Akışkan iki kolon ("hybrid"): media query olmadan da dar ekranda alt alta iner, Outlook masaüstü için
// MSO koşullu tablo. rtl: masaüstünde kolon sırasını çevirir, mobilde kaynak sırası (görsel önce) kalır.
function fluidRow(cols, { bg = '', rtl = false, valign = 'top' } = {}) {
  const cell = (html, i) => `${i ? `<!--[if mso]></td><td width="300" valign="${valign}"><![endif]-->` : ''}<div class="fcol" dir="ltr" style="display:inline-block;width:100%;max-width:300px;vertical-align:${valign};${bg ? `background:${bg};` : ''}">${html}</div>`;
  return `<tr><td dir="${rtl ? 'rtl' : 'ltr'}" align="center" style="padding:0;font-size:0;line-height:0;${bg ? `background:${bg};` : ''}">
<!--[if mso]><table role="presentation" width="600" cellpadding="0" cellspacing="0" dir="${rtl ? 'rtl' : 'ltr'}"><tr><td width="300" valign="${valign}"><![endif]-->
${cols.map(cell).join('')}
<!--[if mso]></td></tr></table><![endif]--></td></tr>`;
}

function productCell(c, item, label, buyLabel, w = 268) {
  if (!item) return `<td class="col" width="${w}" valign="top" style="width:${w}px;padding:0 0 26px;"><div style="background:${c.t.soft};height:${w}px;border-radius:${c.r}px;"></div></td>`;
  const lab = label ? `<div style="font:700 10px/1 ${BODY};letter-spacing:2px;color:${c.t.muted};text-transform:uppercase;margin:0 0 6px;">${rich(label)}</div>` : '';
  const price = item.price != null ? `<span style="font:700 15px/1.3 ${BODY};color:${c.t.ink};">${money(item.price)}</span>`
    + (item.listPrice ? ` <span style="font:400 13px/1.3 ${BODY};color:#9a9a9a;text-decoration:line-through;">${money(item.listPrice)}</span>` : '') : '';
  const buy = buyLabel ? `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-top:12px;"><tr><td align="center" style="background:${c.t.accent};border-radius:${c.r}px;">
<a href="${c.link(item.url)}" style="display:block;padding:12px 0;font:600 13px/1 ${BODY};color:#fff;text-decoration:none;">${rich(buyLabel)}</a></td></tr></table>` : '';
  return `<td class="col" valign="top" width="${w}" style="width:${w}px;padding:0 0 26px;">
<a href="${c.link(item.url)}" style="text-decoration:none;"><img src="${esc(imgOf(item))}" width="${w}" alt="${esc(item.title)}" style="display:block;border:0;width:100%;max-width:${w}px;height:auto;background:${c.t.soft};border-radius:${c.r}px;"></a>
<div style="padding:14px 4px 0;">${lab}
${item.brand ? `<div lang="en" style="font:700 11px/1 ${BODY};letter-spacing:2px;color:${c.t.muted};text-transform:uppercase;margin:0 0 6px;">${esc(item.brand)}</div>` : ''}
<a href="${c.link(item.url)}" style="font:600 15px/1.35 ${BODY};color:${c.t.ink};text-decoration:none;">${esc(item.title)}</a>
${item.color ? `<div style="font:400 13px/1.4 ${BODY};color:${c.t.muted};margin:2px 0 6px;">${esc(item.color)}</div>` : '<div style="height:6px"></div>'}
${price}</div>${buy}</td>`;
}

function pairRows(c, cells) {
  const out = [];
  for (let i = 0; i < cells.length; i += 2) {
    out.push(`<tr>${cells[i]}<td class="gap" width="24" style="width:24px;"></td>${cells[i + 1] || '<td class="col" width="268"></td>'}</tr>`);
  }
  return out.join('');
}

function sectionTitle(c, s, color, align) {
  if (!s) return '';
  return `<div style="font:400 26px/1.1 ${c.head};${c.hstyle(0)}color:${color || c.t.ink};margin:0 0 22px;${align ? `text-align:${align};` : ''}">${rich(s)}</div>`;
}

// Başlık + metin + link üçlüsü (cover / split)
function copyBlock(c, b, dark, align, size) {
  const eb = b.eyebrow ? `<div style="font:500 11px/1 ${BODY};letter-spacing:3px;color:${dark ? '#d9d9d9' : c.t.muted};text-transform:uppercase;margin:0 0 16px;">${rich(b.eyebrow)}</div>` : '';
  const h = b.title ? `<div class="h2" style="font:400 ${size}px/1.15 ${c.head};${c.hstyle(3)}color:${dark ? '#fff' : c.t.ink};margin:0 0 16px;">${rich(b.title)}</div>` : '';
  const p = b.text ? `<div style="font:400 15px/1.7 ${BODY};color:${dark ? '#e3e3e3' : '#4a4a4a'};margin:0 0 ${b.link?.label ? 24 : 0}px;">${rich(b.text)}</div>` : '';
  const l = b.link?.label ? textLink(c, b.link.label, b.link.url || b.url || b.item?.url, dark) : '';
  return `<div style="text-align:${align};">${eb}${h}${p}${l}</div>`;
}

const RENDER = {
  hero(c, b) {
    const bg = bgColor(b.bg, c.t);
    const dark = isDark(bg);
    const eb = b.eyebrow ? `<div style="font:700 11px/1 ${BODY};letter-spacing:3px;color:${dark ? '#bdbdbd' : c.t.muted};text-transform:uppercase;margin:0 0 14px;">${rich(b.eyebrow)}</div>` : '';
    const size = Number(b.titleSize) || 46;
    const h1 = b.title ? `<div class="h1" style="font:400 ${size}px/1.02 ${c.head};color:${dark ? '#fff' : c.t.ink};${c.hstyle(0.5)}margin:0 0 16px;">${rich(b.title)}</div>` : '';
    const p = b.text ? `<div style="font:400 16px/1.55 ${BODY};color:${dark ? '#cfcfcf' : '#333'};margin:0 0 22px;">${rich(b.text)}</div>` : '';
    const btn = b.button?.label ? button(c, b.button.label, b.button.url, dark) : '';
    return `<tr><td class="px" style="background:${bg};padding:44px 32px ${btn ? 36 : 22}px;">${eb}${h1}${p}${btn}</td></tr>`;
  },
  image(c, b) {
    const item = b.item || (b.src ? { image: b.src, jpg: b.jpg, jpgWide: b.jpgWide, url: b.url, title: b.alt || '' } : null);
    if (!item) return `<tr><td style="background:${c.t.soft};height:240px;font:400 13px ${BODY};color:${c.t.muted};" align="center">Pick a product or paste an image URL</td></tr>`;
    const wide = b.crop === 'wide';
    return `<tr><td style="background:${c.t.soft};" align="center"><a href="${c.link(b.url || item.url)}"><img src="${esc(imgOf(item, wide))}" width="600" alt="${esc(item.title || '')}" style="display:block;border:0;width:100%;max-width:600px;height:auto;"></a></td></tr>`;
  },
  cover(c, b) {
    const bg = bgColor(b.bg, c.t);
    const src = picOf(b, false);
    const img = src ? `<tr><td style="background:${c.t.soft};"><a href="${c.link(b.url || b.link?.url || b.item?.url)}"><img src="${esc(src)}" width="600" alt="${esc(b.title || '')}" style="display:block;border:0;width:100%;max-width:600px;height:auto;"></a></td></tr>`
      : `<tr><td style="background:${c.t.soft};height:320px;font:400 13px ${BODY};color:${c.t.muted};" align="center">Pick a product or paste an image URL</td></tr>`;
    const copy = (b.title || b.text || b.eyebrow) ? `<tr><td class="px" style="background:${bg};padding:46px 48px 50px;">${copyBlock(c, b, isDark(bg), b.align || 'center', Number(b.titleSize) || 34)}</td></tr>` : '';
    return `<tr><td style="padding:0;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0">${img}${copy}</table></td></tr>`;
  },
  split(c, b) {
    const bg = bgColor(b.bg, c.t);
    const src = picOf(b, true);
    const pic = src
      ? `<a href="${c.link(b.url || b.link?.url || b.item?.url)}"><img class="fimg" src="${esc(src)}" width="300" alt="${esc(b.title || '')}" style="display:block;border:0;width:100%;height:auto;background:${c.t.soft};"></a>`
      : `<div style="height:400px;background:${c.t.soft};"></div>`;
    const txt = `<div class="fpad" style="padding:36px 34px;font-size:14px;line-height:normal;">${copyBlock(c, b, isDark(bg), 'left', Number(b.titleSize) || 24)}</div>`;
    // görsel her zaman kaynakta önce (mobilde üstte); "sağda" ise masaüstünde rtl ile yer değiştirir
    return `<tr><td style="padding:0;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0">${fluidRow([pic, txt], { bg, rtl: b.side === 'right', valign: 'middle' })}</table></td></tr>`;
  },
  products(c, b) {
    if (b.layout === 'editorial') {
      const bg = bgColor(b.bg, c.t);
      const cells = (b.items || []).map((it) => editorialCell(c, it.item, it.label, b.buyLabel, bg));
      if (!cells.length) cells.push(editorialCell(c, null), editorialCell(c, null));
      const rows = [];
      for (let i = 0; i < cells.length; i += 2) rows.push(fluidRow([cells[i], cells[i + 1] || ''], { bg }));
      const head = b.title ? `<tr><td class="px" style="background:${bg};padding:46px 32px 6px;">${sectionTitle(c, b.title, null, 'center')}</td></tr>` : '';
      return `<tr><td style="padding:0;background:${bg};"><table role="presentation" width="100%" cellpadding="0" cellspacing="0">${head}
<tr><td style="padding:0;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0">${rows.join('')}</table></td></tr></table></td></tr>`;
    }
    const cells = (b.items || []).map((it) => productCell(c, it.item, it.label, b.buyLabel));
    if (!cells.length) cells.push(productCell(c, null), productCell(c, null));
    return `<tr><td class="px" style="background:${bgColor(b.bg, c.t)};padding:36px 32px 10px;">${sectionTitle(c, b.title)}
<table role="presentation" width="100%" cellpadding="0" cellspacing="0">${pairRows(c, cells)}</table></td></tr>`;
  },
  compare(c, b) {
    const head = pairRows(c, [productCell(c, b.a, b.labelA, b.buyLabel), productCell(c, b.b, b.labelB, b.buyLabel)]);
    const rows = (b.rows || []).filter((r) => r && (r[0] || r[1] || r[2])).map(([k, x, y]) => `<tr>
<td style="padding:12px 0;border-top:1px solid ${c.t.line};font:400 13px/1.4 ${BODY};color:${c.t.muted};" width="34%">${rich(k)}</td>
<td style="padding:12px 8px;border-top:1px solid ${c.t.line};font:400 14px/1.4 ${BODY};color:#444;" width="33%">${rich(x)}</td>
<td style="padding:12px 0;border-top:1px solid ${c.t.line};font:700 14px/1.4 ${BODY};color:${c.t.ink};" width="33%">${rich(y)}</td></tr>`).join('');
    return `<tr><td class="px" style="background:#fff;padding:8px 32px 6px;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0">${head}</table>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 30px;">${rows}</table></td></tr>`;
  },
  steps(c, b) {
    const rows = (b.items || []).map((s, i) => `<tr><td valign="top" width="44" style="padding:0 0 18px;font:400 30px/1 ${c.head};color:${c.t.ink};">${String(i + 1).padStart(2, '0')}</td>
<td valign="top" style="padding:2px 0 18px;"><div style="font:700 15px/1.3 ${BODY};color:${c.t.ink};margin:0 0 4px;">${rich(s.title)}</div>
<div style="font:400 14px/1.5 ${BODY};color:#444;">${rich(s.text)}</div></td></tr>`).join('');
    return `<tr><td class="px" style="background:${bgColor(b.bg, c.t)};padding:36px 32px 22px;">${sectionTitle(c, b.title)}<table role="presentation" width="100%" cellpadding="0" cellspacing="0">${rows}</table></td></tr>`;
  },
  text(c, b) {
    const bg = bgColor(b.bg, c.t);
    const dark = isDark(bg);
    const st = b.small ? `font:400 13px/1.6 ${BODY};color:${dark ? '#bdbdbd' : c.t.muted};` : `font:400 16px/1.55 ${BODY};color:${dark ? '#e5e5e5' : '#333'};`;
    return `<tr><td class="px" style="background:${bg};padding:${b.small ? '22px' : '28px'} 32px;"><div style="${st}">${rich(b.text)}</div></td></tr>`;
  },
  button(c, b) {
    const bg = bgColor(b.bg, c.t);
    return `<tr><td class="px" style="background:${bg};padding:8px 32px 40px;">${button(c, b.label, b.url, isDark(bg))}</td></tr>`;
  },
  spacer(c, b) {
    return `<tr><td style="background:${bgColor(b.bg, c.t)};height:${Number(b.height) || 24}px;line-height:1px;font-size:1px;">&nbsp;</td></tr>`;
  },
};

function header(c) {
  const t = c.t;
  const logo = t.logo
    ? `<img src="${esc(t.logo)}" width="${t.logoWidth}" alt="logo" style="display:block;border:0;width:${t.logoWidth}px;height:auto;">`
    : '<div style="font:700 22px Arial;letter-spacing:2px;">LOGO</div>';
  // menü: satır içi bağlantılar (dar ekranda gerekirse alt satıra geçer, tablo gibi taşmaz)
  const nav = (t.nav || []).filter((n) => n.label).map((n) => `<a class="navl" href="${c.link(n.url)}" style="display:inline-block;padding:0 12px;font:600 11px/19px ${BODY};letter-spacing:2px;color:${t.ink};text-decoration:none;text-transform:uppercase;">${esc(n.label)}</a>`).join('');
  return `<tr><td style="background:#fff;padding:24px 32px ${nav ? 18 : 24}px;" align="center"><a href="${c.link(t.homeUrl || '')}">${logo}</a></td></tr>`
    + (nav ? `<tr><td class="px" style="background:#fff;padding:0 32px 18px;border-bottom:1px solid ${t.line};font-size:0;line-height:0;" align="center">${nav}</td></tr>` : '');
}

function footer(c) {
  const t = c.t;
  if (t.footerStyle === 'light') {
    const links = (t.footerLinks || []).filter((l) => l.label).map((l) => `<a href="${c.link(l.url)}" style="color:${t.ink};text-decoration:none;">${esc(l.label)}</a>`).join('&nbsp;&nbsp;&nbsp;&nbsp;');
    return `<tr><td class="px" style="background:${t.soft};padding:44px 32px 46px;border-top:1px solid ${t.line};" align="center">
${t.logo ? `<img src="${esc(t.logo)}" width="110" alt="" style="display:block;border:0;width:110px;height:auto;margin:0 auto 22px;">` : ''}
${links ? `<div style="font:500 11px/1.8 ${BODY};letter-spacing:3px;color:${t.ink};margin:0 0 18px;text-transform:uppercase;">${links}</div>` : ''}
<div style="font:400 12px/1.7 ${BODY};color:${t.muted};">${rich(t.footerText)}<br>
<a href="{{unsubscribe_url}}" style="color:${t.muted};">E-posta aboneliğinden çık</a></div></td></tr>`;
  }
  const logo = t.logoFooter || t.logo;
  const links = (t.footerLinks || []).filter((l) => l.label).map((l) => `<a href="${c.link(l.url)}" style="color:#fff;text-decoration:none;">${esc(l.label)}</a>`).join('&nbsp;&nbsp;&nbsp;&nbsp;');
  return `<tr><td class="px" style="background:${t.ink};padding:34px 32px 38px;" align="center">
${logo ? `<img src="${esc(logo)}" width="64" alt="" style="display:block;border:0;width:64px;height:auto;margin:0 auto 18px;${t.logoFooter ? '' : 'filter:invert(1);'}">` : ''}
${links ? `<div style="font:600 12px/1.8 ${BODY};letter-spacing:2px;color:#fff;margin:0 0 16px;text-transform:uppercase;">${links}</div>` : ''}
<div style="font:400 12px/1.6 ${BODY};color:${t.footerMuted || '#8a8a8a'};">${rich(t.footerText)}<br>
<a href="{{unsubscribe_url}}" style="color:${t.footerMuted || '#bdbdbd'};">E-posta aboneliğinden çık</a></div></td></tr>`;
}

const CSS = `body{margin:0;padding:0;-webkit-text-size-adjust:100%;}
img{-ms-interpolation-mode:bicubic;} a{color:inherit;}
@media (max-width:620px){
 .wrap{width:100%!important;}
 .px{padding-left:20px!important;padding-right:20px!important;}
 .h1{font-size:36px!important;}
 .h2{font-size:26px!important;}
 .col{display:block!important;width:100%!important;max-width:100%!important;}
 .col img{max-width:100%!important;}
 .gap{display:none!important;}
 .fcol{max-width:100%!important;}
 .navl{padding:4px 7px!important;letter-spacing:1.5px!important;}
 .fimg{max-width:100%!important;width:100%!important;}
 .fpad{padding:30px 22px 34px!important;}
}`;

// opts.selectable: stüdyo önizlemesi — her blok tıklanınca üst pencereye kimliğini yollar
export function renderEmail(doc, opts = {}) {
  const c = ctx(doc, opts);
  const font = (HEAD_FONTS[c.t.headFont] || HEAD_FONTS.Anton).url;
  const blocks = (doc.blocks || []).map((b) => {
    const html = (RENDER[b.type] || (() => ''))(c, b);
    if (!opts.selectable) return html;
    // <tr> içine işaret: tbody'de seçim için data-block
    return html.replace(/<tr>/, `<tr data-block="${b.id}" class="gm-b${opts.selected === b.id ? ' gm-sel' : ''}">`);
  }).join('\n');
  const studioCss = opts.selectable
    ? `.gm-b{cursor:pointer} .gm-b:hover>td{outline:2px dashed #818cf8;outline-offset:-2px} .gm-sel>td{outline:2px solid #4f46e5!important;outline-offset:-2px}`
    : '';
  const studioJs = opts.selectable
    ? `<script>document.addEventListener('click',function(e){var r=e.target.closest('[data-block]');e.preventDefault();parent.postMessage({gmBlock:r?r.getAttribute('data-block'):null},'*');},true);</script>`
    : '';
  return `<!doctype html>
<html lang="tr"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<meta name="x-apple-disable-message-reformatting">
<title>${esc(opts.subject || '')}</title>${font ? `<link href="${font}" rel="stylesheet">` : ''}<style>${CSS}${studioCss}</style></head>
<body style="margin:0;padding:0;background:${c.t.pageBg || '#e9e9e9'};"><div style="display:none;max-height:0;overflow:hidden;">${esc(opts.preheader || '')}&#8199;&#65279;&#847;&#8199;&#65279;&#847;</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${c.t.pageBg || '#e9e9e9'};"><tr><td align="center" style="padding:24px 0;">
<!--[if mso]><table role="presentation" width="600" cellpadding="0" cellspacing="0" align="center"><tr><td><![endif]-->
<table role="presentation" class="wrap" width="100%" cellpadding="0" cellspacing="0" style="width:100%;max-width:600px;background:#fff;">
${header(c)}${blocks}${footer(c)}
</table><!--[if mso]></td></tr></table><![endif]--></td></tr></table>${studioJs}</body></html>`;
}

// Kaydetmeden önce JPG'ye çevrilmesi gereken görseller: [{ url, w, crop, apply(jpgUrl) }]
export function collectImages(doc) {
  const out = [];
  const want = (item, wide) => {
    if (!item?.image) return;
    const key = wide ? 'jpgWide' : 'jpg';
    if (item[key]) return;
    out.push({ url: item.image, w: wide ? 1200 : 600, crop: wide ? 'wide' : 'square', apply: (u) => { item[key] = u; } });
  };
  for (const b of doc.blocks || []) {
    if (b.type === 'image') {
      if (b.item) want(b.item, b.crop === 'wide');
      else if (b.src && !/\.(jpe?g|png|gif)(\?|$)/i.test(b.src)) {
        const wide = b.crop === 'wide';
        const key = wide ? 'jpgWide' : 'jpg';
        if (!b[key]) out.push({ url: b.src, w: wide ? 1200 : 600, crop: wide ? 'wide' : 'square', apply: (u) => { b[key] = u; } });
      }
    }
    if (b.type === 'cover' || b.type === 'split') {
      if (b.item) want(b.item, b.type === 'cover');
      else if (b.src && !b.jpg && !/\.(jpe?g|png|gif)(\?|$)/i.test(b.src)) out.push({ url: b.src, w: 1200, crop: 'square', apply: (u) => { b.jpg = u; } });
    }
    if (b.type === 'products') (b.items || []).forEach((it) => want(it.item, false));
    if (b.type === 'compare') { want(b.a, false); want(b.b, false); }
  }
  return out;
}

// Belgede kullanılan {{etiket}} adları (stüdyodaki "kişi alanları" listesi için)
export function usedTags(doc) {
  const s = JSON.stringify(doc);
  const set = new Set();
  s.replace(/\{\{\s*([a-zA-Z0-9_.]+)/g, (m, k) => { if (k !== 'unsubscribe_url') set.add(k); return m; });
  return [...set];
}

// Katalogdan gelen ürünü belgeye yazılacak biçime getir ("Ad 'Renk'" → title + color)
export function itemFromProduct(p) {
  const m = /\s*'([^']*)'\s*$/.exec(p.title || '');
  return {
    title: (p.title || '').replace(/\s*'[^']*'\s*$/, '').replace(/^Zoom /, ''),
    color: m ? m[1] : '',
    brand: p.brand || '',
    url: p.url,
    image: p.image,
    price: p.price ?? null,
    listPrice: p.listPrice ?? null,
  };
}
